import { useState, useEffect } from "react";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import AppLayout from "@cloudscape-design/components/app-layout";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import SideNavigation from "@cloudscape-design/components/side-navigation";
import Toggle from "@cloudscape-design/components/toggle";
import Select from "@cloudscape-design/components/select";
import Box from "@cloudscape-design/components/box";
import Spinner from "@cloudscape-design/components/spinner";
import { useTranslation } from "react-i18next";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginView from "./components/LoginView";
import ScenariosView from "./components/ScenariosView";
import ConversationView from "./components/ConversationView";
import GatewayTargetsView from "./components/GatewayTargetsView";
import SessionsView from "./components/SessionsView";
import SessionDetailView from "./components/SessionDetailView";
import HomeView from "./components/HomeView";
import NextStepsView from "./components/NextStepsView";
import "./App.css";

function AppContent() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading, user, signOut, isWriteUser } = useAuth();
  
  const [currentView, setCurrentView] = useState("home");
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [darkMode, setDarkMode] = useState(localStorage.getItem("theme") === "dark");
  const [selectedLanguage, setSelectedLanguage] = useState({
    label: getLanguageName(i18n.language),
    value: i18n.language,
  });

  function getLanguageName(lng) {
    const names = { en: "English", es: "Español", ja: "日本語", "pt-BR": "Português (BR)", fr: "Français", de: "Deutsch", it: "Italiano", zh: "中文", ko: "한국어" };
    return names[lng] || lng;
  }

  useEffect(() => {
    applyMode(darkMode ? Mode.Dark : Mode.Light);
  }, [darkMode]);

  const handleDarkModeToggle = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
  };

  const handleLanguageChange = ({ detail }) => {
    setSelectedLanguage(detail.selectedOption);
    i18n.changeLanguage(detail.selectedOption.value);
  };

  const handleNavigate = (view, data = null) => {
    if (view === "home") {
      setCurrentView("home");
      setSelectedScenario(null);
      setSessionId(null);
      setSelectedSession(null);
    } else if (view === "scenarios") {
      setCurrentView("scenarios");
      setSelectedScenario(null);
      setSessionId(null);
    } else if (view === "sessions") {
      setCurrentView("sessions");
      setSelectedScenario(null);
      setSessionId(null);
      setSelectedSession(null);
    } else if (view === "gateway-targets") {
      setCurrentView("gateway-targets");
      setSelectedScenario(null);
      setSessionId(null);
    } else if (view === "next-steps") {
      setCurrentView("next-steps");
      setSelectedScenario(null);
      setSessionId(null);
    } else if (view === "conversation" && data?.scenario) {
      setSelectedScenario(data.scenario);
      const callSessionId = data.callResponse?.session_id || 
                            data.callResponse?.call_details?.call_sid ||
                            `session-${Date.now()}`;
      setSessionId(callSessionId);
      setCurrentView("conversation");
    } else if (view === "session-detail" && data?.session) {
      setSelectedSession(data.session);
      setCurrentView("session-detail");
    }
  };

  const languageOptions = [
    { label: "English", value: "en" },
    { label: "Español", value: "es" },
    { label: "Français", value: "fr" },
    { label: "Deutsch", value: "de" },
    { label: "Italiano", value: "it" },
    { label: "Português (BR)", value: "pt-BR" },
    { label: "中文", value: "zh" },
    { label: "日本語", value: "ja" },
    { label: "한국어", value: "ko" },
  ];

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginView />;
  }

  const navigationItems = [
    { type: "link", text: t("home.nav.title"), href: "#/home" },
    { type: "link", text: t("scenarios.nav.title"), href: "#/scenarios" },
    { type: "link", text: t("sessions.nav.title"), href: "#/sessions" },
    ...(isWriteUser ? [{ type: "link", text: t("gateway-targets.nav.title"), href: "#/gateway-targets" }] : []),
    { type: "link", text: t("next-steps.nav.title"), href: "#/next-steps" },
    { type: "divider" },
    {
      type: "link",
      text: (
        <Box>
          <Toggle checked={darkMode} onChange={handleDarkModeToggle}>
            {t("app.common.dark-mode")}
          </Toggle>
        </Box>
      ),
      href: "#",
    },
    {
      type: "link",
      text: (
        <Box>
          <Select
            selectedOption={selectedLanguage}
            onChange={handleLanguageChange}
            options={languageOptions}
            placeholder={t("app.common.language")}
          />
        </Box>
      ),
      href: "#",
    },
  ];

  const renderContent = () => {
    if (currentView === "home") {
      return <HomeView onNavigate={handleNavigate} />;
    }
    if (currentView === "next-steps") {
      return <NextStepsView />;
    }
    if (currentView === "conversation" && selectedScenario && sessionId) {
      return (
        <ConversationView
          scenario={selectedScenario}
          sessionId={sessionId}
          onEnd={() => handleNavigate("scenarios")}
        />
      );
    }
    if (currentView === "session-detail" && selectedSession) {
      return (
        <SessionDetailView
          session={selectedSession}
          onBack={() => handleNavigate("sessions")}
        />
      );
    }
    if (currentView === "sessions") {
      return <SessionsView onNavigate={handleNavigate} />;
    }
    if (currentView === "gateway-targets" && isWriteUser) {
      return <GatewayTargetsView />;
    }
    if (currentView === "scenarios") {
      return <ScenariosView onNavigate={handleNavigate} />;
    }
    return <HomeView onNavigate={handleNavigate} />;
  };

  return (
    <>
      <TopNavigation
        identity={{
          href: "#",
          logo: {
            src: "/amazon-nova-sonic-logo-white.svg",
            alt: "Amazon Nova Sonic",
          },
        }}
        utilities={[
          {
            type: "menu-dropdown",
            text: user?.email || "",
            iconName: "user-profile",
            items: [
              { id: "signout", text: t("auth.login.sign-out") }
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === "signout") signOut();
            }
          }
        ]}
        i18nStrings={{ overflowMenuTriggerText: "More" }}
      />
      <AppLayout
        navigation={
          <SideNavigation
            header={{ text: t("app.common.menu"), href: "#" }}
            items={navigationItems}
            activeHref={`#/${currentView}`}
            onFollow={(e) => {
              e.preventDefault();
              if (e.detail.href === "#/home") {
                handleNavigate("home");
              } else if (e.detail.href === "#/scenarios") {
                handleNavigate("scenarios");
              } else if (e.detail.href === "#/sessions") {
                handleNavigate("sessions");
              } else if (e.detail.href === "#/gateway-targets") {
                handleNavigate("gateway-targets");
              } else if (e.detail.href === "#/next-steps") {
                handleNavigate("next-steps");
              }
            }}
          />
        }
        content={renderContent()}
        toolsHide
        navigationWidth={280}
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
